import { useId } from 'react';
import { Input, inputBase } from '@/components/ui/input';
import { cn } from '@/lib/utils';

// Label-above-input form row. The label is ALWAYS visible (per
// accessibility rule "labels are visible, not placeholders").
// Error state pairs clay-red border + helper text — color + text + position
// is the AA-acceptable triple signal.

interface InspectorFieldProps {
  label: string;
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  helperText?: string;
  errorText?: string;
  multiline?: boolean;
  rows?: number;
  type?: 'text' | 'url' | 'email' | 'tel';
}

export function InspectorField({
  label,
  value,
  onChange,
  placeholder,
  helperText,
  errorText,
  multiline = false,
  rows = 3,
  type = 'text',
}: InspectorFieldProps) {
  const id = useId();
  const describedById = helperText || errorText ? `${id}-desc` : undefined;
  const hasError = Boolean(errorText);

  // T12/B2: one recipe. The error state keeps its clay-red border on top of it.
  const errorClasses = hasError && 'border-danger';

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium text-foreground" htmlFor={id}>
        {label}
      </label>
      {multiline ? (
        <textarea
          id={id}
          rows={rows}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={hasError || undefined}
          aria-describedby={describedById}
          className={cn(inputBase, 'py-2 leading-relaxed resize-y min-h-[5rem]', errorClasses)}
        />
      ) : (
        <Input
          id={id}
          size="sm"
          type={type}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={hasError || undefined}
          aria-describedby={describedById}
          className={cn(errorClasses)}
        />
      )}
      {hasError ? (
        <p id={describedById} className="text-xs text-danger-fg">
          {errorText}
        </p>
      ) : helperText ? (
        <p id={describedById} className="text-xs text-text-tertiary">
          {helperText}
        </p>
      ) : null}
    </div>
  );
}
